import { useCallback, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  SelectOption,
  type StringIndexedObject,
  useForm,
} from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import { Credit, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useOverrideJob from 'hooks/useOverrideJob';
import useReloadInitialData from 'hooks/useReloadInitialData';
import { selectors } from 'store';
import paymentSelectors from 'store/selectors/payments';

import { formatFinanceSelectOptions } from './helpers';

import { PropensioCallback } from '../usePaymentOverrideButtons';

export interface VerifyCompanyFormValues {
  aprChanged: 'yes' | 'no';
  rate: string;
  deposit?: string;
}

export interface VerifyCompanyFormHook {
  form: UseFormReturn<VerifyCompanyFormValues>;
  isSubmitting: boolean;
  aprChanged: boolean;
  onSubmit: (formValues: StringIndexedObject) => void;
  rateOptions: SelectOption[];
}

export default function useVerifyCompanyForm(
  handleClose: () => void,
  propensioCallback: PropensioCallback | undefined,
  setPropensioCallback: React.Dispatch<
    React.SetStateAction<PropensioCallback | undefined>
  >,
): VerifyCompanyFormHook {
  const dispatch = useAppDispatch();

  const rates = useSelector(selectors.rate.getEntities);
  const refreshAll = useReloadInitialData();
  const job = useSelector(paymentSelectors.getPaymentJob);
  const credit = useSelector(paymentSelectors.getPaymentCredit);

  const form = useForm<VerifyCompanyFormValues>({
    mode: 'onBlur',
    defaultValues: { deposit: credit?.deposit_value?.amount?.toString() },
  });

  const availablePropensioRates = useMemo(
    () =>
      rates['all']?.finance_rates.filter(
        (rate) => rate.provider === 'propensio',
      ) || [],
    [rates],
  );

  const rateOptions = useMemo(
    () => formatFinanceSelectOptions(availablePropensioRates),
    [availablePropensioRates],
  );

  const { showSuccess, showError } = useToast();

  const [aprChangedValue, rateName, deposit]: [
    'yes' | 'no',
    string,
    string | undefined,
  ] = useWatch({
    name: ['aprChanged', 'rate', 'deposit'],
    control: form.control,
  });

  const aprChanged = useMemo(
    () => aprChangedValue === 'yes',
    [aprChangedValue],
  );

  const rate = useMemo(
    () => availablePropensioRates.find((rate) => rate.name === rateName),
    [rateName, rateOptions],
  );

  const { isSubmitting, overrideJob } = useOverrideJob();

  const onSubmit = useCallback(
    (formValues: StringIndexedObject) => {
      // Extract info and check
      if (!propensioCallback) return;
      const creditId = credit?.id;
      const jobId = job?.id;
      const xid = propensioCallback === 'cancel' ? creditId : formValues?.xid;
      if (!creditId || !jobId || !xid) return;
      const body = {
        ...credit,
        xid,
      };
      // Submit
      dispatch(
        actions.putCredit({
          body: body as Credit,
          params: { id: creditId as string },
          onError: () => {
            handleClose();
          },
          onSuccess: () =>
            overrideJob(
              jobId,
              xid,
              propensioCallback,
              () => {
                refreshAll();
                setPropensioCallback(undefined);
                handleClose();
              },
              rate,
              deposit ? parseInt(deposit, 10) : 0,
              aprChanged,
            ),
        }),
      );
    },
    [
      credit,
      job,
      actions.putCredit,
      overrideJob,
      refreshAll,
      showError,
      showSuccess,
      handleClose,
    ],
  );

  return {
    form,
    isSubmitting,
    aprChanged,
    onSubmit,
    rateOptions,
  };
}
